import { type UserReviews_userProfile$data } from '#app/__generated__/relay/UserReviews_userProfile.graphql.js';
import { Avatar } from '#app/components/Avatars/Avatar/Avatar.js';
import { AvatarGroup } from '#app/components/Avatars/AvatarGroup/AvatarGroup.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useNavigation } from '#app/hooks/useNavigation.js';
import { formatName } from '#app/utilities/formatName.js';
import { MoreReviews } from './MoreReviews.js';
import * as styles from './UserReviewCard.styles.js';
import { UserReviewText } from './UserReviewText.js';
import { dayjs } from '@contra/date';
import { getRoute } from '@contra/routes';

type ReviewDetails = Pick<
  UserReviews_userProfile$data['userReviews']['edges'][0]['node'],
  'associatedWork' | 'author' | 'createdAt' | 'reviewText'
> & {
  readonly hasReviewText: boolean;
};

type UserReviewCardProps = {
  readonly reviews: ReviewDetails[];
};

export const UserReviewCard = ({
  reviews,
}: UserReviewCardProps): JSX.Element => {
  // get the reviews which have review text to the top
  const sortedReviews = reviews.toSorted((review) =>
    review.hasReviewText ? -1 : 1,
  );
  const { associatedWork, author, createdAt, reviewText } = sortedReviews[0];

  const navigation = useNavigation();
  const dateFormatter = dayjs(createdAt);
  const { track } = useAnalytics();

  const authorRoleOnProject = associatedWork.paidProjectProfile
    ? associatedWork.paidProjectProfile.contractor.id === author.id
      ? 'Freelancer'
      : 'Client'
    : 'Client';

  const authorFullName = formatName(
    { firstName: author.firstName, lastName: author.lastName },
    'fullName',
  );

  const companyName = author.organization?.name;

  const shouldShowCompany =
    Boolean(companyName) && authorRoleOnProject === 'Client';

  const avatarGroupData = [
    {
      avatarImage: author.avatarImage,
      key: 'authorAvatar',
      onClick: () => {
        navigation.push(
          getRoute('/@username', {
            args: { username: author.displayUsername },
          }),
        );
      },
    },
    {
      avatarImage: shouldShowCompany ? author.organization?.logoImage : null,
      key: 'companyAvatar',
    },
  ].filter(({ avatarImage }) => Boolean(avatarImage));

  return (
    <styles.Container>
      <styles.AuthorContainer>
        {avatarGroupData.length > 0 ? (
          <AvatarGroup
            data={avatarGroupData}
            size="sm"
            useAlternativeStackingOrder
          />
        ) : (
          // show the avatar placeholder if there are no avatars available
          <Avatar
            hidePlaceholderOverflow
            size="sm"
          />
        )}
        <div>
          <styles.HeaderText
            onClick={() => {
              track('link', {
                action: 'tapped',
                destination_path: author.displayUsername,
                destination_route: 'user',
                link_name: 'identity_recommendation_author',
              });

              navigation.push(
                getRoute('/@username', {
                  args: { username: author.displayUsername },
                }),
              );
            }}
          >
            {authorFullName}
            {shouldShowCompany && ` • ${companyName}`}
          </styles.HeaderText>
          <styles.WorkDate>
            {`${authorRoleOnProject} • ${dateFormatter.format('MMM D, YYYY')}`}
          </styles.WorkDate>
        </div>
      </styles.AuthorContainer>

      <UserReviewText
        authorRoleOnProject={authorRoleOnProject}
        createdAt={createdAt}
        reviewText={reviewText}
      />

      <MoreReviews
        authorRoleOnProject={authorRoleOnProject}
        reviews={sortedReviews}
      />
    </styles.Container>
  );
};
